import { useAuth } from "@/contexts/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UserCheck, LogOut, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * Persistent orange banner shown at the top of every page while an admin
 * is impersonating a resident/vendor. Exit hits `/api/admin/impersonate/end`
 * which restores the admin's httpOnly session cookie server-side. No JS
 * token handling — the browser only ever sees the httpOnly cookies.
 */
export function ImpersonationBanner() {
  const { user } = useAuth();
  if (!user || !user.impersonated_by) return null;
  const act = user.impersonated_by;

  const exit = async () => {
    try {
      const { data } = await api.post("/admin/impersonate/end");
      if (data.session_restored === false) {
        toast.warning("Admin session expired — please log in again");
      } else {
        toast.success("Returned to your admin session");
      }
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to exit impersonation");
    }
    // Single full navigation resets every in-memory cache.
    window.location.assign("/admin/dashboard");
  };

  return (
    <div
      className="sticky top-0 z-[60] w-full flex items-center justify-center gap-3 px-4 py-2 text-sm bg-orange-500 text-white shadow-md"
      data-testid="impersonation-banner"
      role="status"
      aria-live="polite"
    >
      <ShieldAlert className="h-4 w-4 shrink-0" />
      <span className="truncate">
        <b>Impersonating {user.name || user.email}</b>
        <span className="opacity-80 hidden md:inline">
          {" "}
          - you&apos;re seeing what {user.name?.split(" ")[0] || "they"} sees, signed in as {act.email}.
        </span>
      </span>
      <Button
        size="sm"
        variant="outline"
        className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white shrink-0"
        onClick={exit}
        data-testid="impersonation-exit"
      >
        <LogOut className="h-3.5 w-3.5 mr-1" /> Exit
      </Button>
      <UserCheck className="h-4 w-4 opacity-70 shrink-0 hidden md:block" />
    </div>
  );
}
